import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { FileText, RefreshCw, Save } from "lucide-react";
import { Card, Btn } from "@/components/admin/ui";
import { API } from "@/config";

// Canonical list of editable keys with defaults + short admin-facing labels.
const CONTENT_KEYS = [
  {
    section: "Hero",
    keys: [
      { key: "hero.eyebrow", label: "Hero eyebrow", fallback: "Free delivery, everywhere.", multi: false },
      { key: "hero.subtitle_prefix", label: "Hero subtitle (prefix)", fallback: "Baby gear, delivered to your ", multi: false },
      { key: "hero.subtitle_highlight", label: "Hero subtitle (gold highlight)", fallback: "Walt Disney World", multi: false },
      { key: "hero.subtitle_suffix", label: "Hero subtitle (suffix)", fallback: " resort.", multi: false },
    ],
  },
  {
    section: "Why Enchanted — intro",
    keys: [
      { key: "whyus.eyebrow", label: "Section eyebrow", fallback: "Why Enchanted · A new chapter, same trusted hands", multi: false },
      { key: "whyus.headline", label: "Section headline", fallback: "A fresh bayou-born brand, built on 5,000 magical trips.", multi: true },
      { key: "whyus.about_paragraph", label: "About paragraph", fallback: "Enchanted Gear Rentals is a new name — but not a new team. We've spent years delivering strollers and cribs to Disney families through another platform. Now we're planting roots in our own bayou, with the same premium gear, the same white-glove care, and every lesson from 5,000+ reservations baked in.", multi: true },
    ],
  },
  {
    section: "Stats (big numbers row)",
    keys: [
      { key: "stats.1.value", label: "Stat 1 value", fallback: "5,000+", multi: false },
      { key: "stats.1.label", label: "Stat 1 label", fallback: "Reservations delivered", multi: false },
      { key: "stats.2.value", label: "Stat 2 value", fallback: "500+", multi: false },
      { key: "stats.2.label", label: "Stat 2 label", fallback: "Five-star families", multi: false },
      { key: "stats.3.value", label: "Stat 3 value", fallback: "24hr", multi: false },
      { key: "stats.3.label", label: "Stat 3 label", fallback: "Same-day booking", multi: false },
      { key: "stats.4.value", label: "Stat 4 value", fallback: "100%", multi: false },
      { key: "stats.4.label", label: "Stat 4 label", fallback: "Sanitized between rentals", multi: false },
    ],
  },
  {
    section: "Why Enchanted — cards",
    keys: [
      { key: "whyus.card1.title", label: "Card 1 title", fallback: "5,000+ reservations of experience", multi: false },
      { key: "whyus.card1.copy",  label: "Card 1 copy",  fallback: "We're new to our own brand — but not to this magic. After 5,000+ successful family reservations on another platform, we're planting roots in the bayou and bringing every lesson with us.", multi: true },
      { key: "whyus.card2.title", label: "Card 2 title", fallback: "Family-owned, family-first", multi: false },
      { key: "whyus.card2.copy",  label: "Card 2 copy",  fallback: "You're not booking through a warehouse call center. Every drop-off, every crib linen, every text back — that's us. We know the difference a well-set-up crib makes at 9pm after a park day.", multi: true },
      { key: "whyus.card3.title", label: "Card 3 title", fallback: "Premium gear, hospital-grade clean", multi: false },
      { key: "whyus.card3.copy",  label: "Card 3 copy",  fallback: "UPPAbaby, Graco, and top-brand cribs — inspected and sanitized after every rental. Fresh laundered linens on every crib and pack 'n play, no exceptions.", multi: true },
      { key: "whyus.card4.title", label: "Card 4 title", fallback: "Bell services drop-off, contactless", multi: false },
      { key: "whyus.card4.copy",  label: "Card 4 copy",  fallback: "For Walt Disney World resorts we personally meet you in person. Bonnet Creek, Universal, Disney Springs Area hotels — we drop off at bell services so it's ready at check-in. Airbnbs and vacation homes — we meet you at the door.", multi: true },
      { key: "whyus.card5.title", label: "Card 5 title", fallback: "We know Disney families", multi: false },
      { key: "whyus.card5.copy",  label: "Card 5 copy",  fallback: "Which strollers fit the monorail? Which cribs work in DVC studios? Which stroller handles the Boardwalk best? We've answered these thousands of times. Ask us anything.", multi: true },
      { key: "whyus.card6.title", label: "Card 6 title", fallback: "Local, independent, rooted here", multi: false },
      { key: "whyus.card6.copy",  label: "Card 6 copy",  fallback: "We're an Orlando small business — no franchise, no middleman fees. Every dollar you spend goes back into fresher gear, cleaner sanitization, and more magical hand-offs.", multi: true },
    ],
  },
  {
    section: "Trip Setup section (above catalog)",
    keys: [
      { key: "tripsetup.section.eyebrow", label: "Section eyebrow", fallback: "Where & when", multi: false },
      { key: "tripsetup.section.headline_unset", label: "Headline before trip is set", fallback: "Start here — pick dates, times & delivery.", multi: false },
      { key: "tripsetup.section.headline_set", label: "Headline after trip is set", fallback: "Your trip is set — pick your gear below.", multi: false },
      { key: "tripsetup.cta.title", label: "CTA button title", fallback: "Choose dates, times & delivery", multi: false },
      { key: "tripsetup.cta.blurb", label: "CTA button subtext", fallback: "Unlock the catalog with your trip details", multi: false },
    ],
  },
  {
    section: "Gear Catalog",
    keys: [
      { key: "catalog.eyebrow", label: "Catalog eyebrow", fallback: "Curated gear · Twilight-tested", multi: false },
      { key: "catalog.headline", label: "Catalog headline", fallback: "Everything little wanderers need.", multi: false },
      { key: "catalog.tile.strollers.title", label: "Strollers tile · title", fallback: "Strollers", multi: false },
      { key: "catalog.tile.strollers.blurb", label: "Strollers tile · blurb", fallback: "Singles, doubles & twins", multi: false },
      { key: "catalog.tile.baby-gear.title", label: "Baby Gear tile · title", fallback: "Baby Gear", multi: false },
      { key: "catalog.tile.baby-gear.blurb", label: "Baby Gear tile · blurb", fallback: "Sleep, meal, bath & play", multi: false },
      { key: "catalog.tile.ecvs.title", label: "ECVs tile · title", fallback: "ECVs", multi: false },
      { key: "catalog.tile.ecvs.blurb", label: "ECVs tile · blurb", fallback: "Electric vehicles — soon", multi: false },
    ],
  },
  {
    section: "Booking Widget (Confirm & pay)",
    keys: [
      { key: "booking.eyebrow", label: "Section eyebrow", fallback: "Confirm & pay", multi: false },
      { key: "booking.headline", label: "Section headline", fallback: "A few details and you're done.", multi: false },
    ],
  },
  {
    section: "Delivery section",
    keys: [
      { key: "delivery.eyebrow", label: "Delivery eyebrow", fallback: "Delivery · Every corner of the Kingdom", multi: false },
      { key: "delivery.headline", label: "Delivery headline", fallback: "We meet you where the magic happens.", multi: false },
      { key: "delivery.paragraph", label: "Delivery paragraph", fallback: "Two delivery styles depending on where you're staying. Disney resorts + Airbnb/VRBO + Cabana Bay & Endless Summer: we meet you in person within your chosen window. Bonnet Creek, Disney Springs Area hotels, and most Universal properties: contactless drop-off at bell services — just ask at check-in. Free delivery on Walt Disney World & bookings over $75.", multi: true },
      { key: "delivery.stat1.value", label: "Stat 1 value", fallback: "24 hrs", multi: false },
      { key: "delivery.stat1.label", label: "Stat 1 label", fallback: "Advance booking", multi: false },
      { key: "delivery.stat2.value", label: "Stat 2 value", fallback: "7 days", multi: false },
      { key: "delivery.stat2.label", label: "Stat 2 label", fallback: "Week-long service", multi: false },
    ],
  },
  {
    section: "Testimonials",
    keys: [
      { key: "testimonials.eyebrow", label: "Testimonials eyebrow", fallback: "Stories from the bayou", multi: false },
      { key: "testimonials.headline", label: "Testimonials headline", fallback: "Families who found the magic waiting.", multi: false },
    ],
  },
  {
    section: "Blog / Field Journal",
    keys: [
      { key: "blog.eyebrow", label: "Blog eyebrow", fallback: "The Field Journal", multi: false },
      { key: "blog.headline", label: "Blog headline", fallback: "Bayou-wisdom for Disney families.", multi: false },
    ],
  },
  {
    section: "FAQ section",
    keys: [
      { key: "faq.eyebrow", label: "FAQ eyebrow", fallback: "Common questions", multi: false },
      { key: "faq.headline", label: "FAQ headline", fallback: "Answered by the willow.", multi: false },
      { key: "faq.contact_line", label: "'Still curious?' text before link", fallback: "Still curious?", multi: false },
      { key: "faq.contact_cta", label: "Contact link text", fallback: "Ask us anything", multi: false },
    ],
  },
  {
    section: "Contact section",
    keys: [
      { key: "contact.eyebrow", label: "Contact eyebrow", fallback: "Get in touch", multi: false },
      { key: "contact.headline", label: "Contact headline", fallback: "Whisper to the willow.", multi: false },
      { key: "contact.paragraph", label: "Contact paragraph", fallback: "Custom bundles, group trips, or last-minute magic — we love making Disney easier.", multi: true },
      { key: "contact.service_area", label: "Service-area line", fallback: "Serving all of Central Florida", multi: false },
    ],
  },
  {
    section: "Footer",
    keys: [
      { key: "footer.tagline", label: "Tagline (top-left)", fallback: "Family-owned baby gear rentals rooted in the enchanted bayous of Florida — delivered with love to your Disney doorstep.", multi: true },
      { key: "footer.email", label: "Contact email", fallback: "info@enchantedgearrentals.com", multi: false },
      { key: "footer.phone", label: "Contact phone", fallback: "Call or Text: 689-345-RENT (7368)", multi: false },
      { key: "footer.city", label: "City line", fallback: "Orlando, Florida", multi: false },
      { key: "footer.copyright", label: "Copyright line", fallback: "© 2026 Enchanted Gear Rentals · Not affiliated with The Walt Disney Company", multi: false },
      { key: "footer.made_with", label: "'Handcrafted in the bayou' line", fallback: "Handcrafted in the bayou", multi: false },
    ],
  },
  {
    section: "Legal — Terms of Service",
    keys: [
      { key: "legal.terms", label: "Full Terms & Cancellation Policy (shown in checkout modal)", multi: true, fallback: "ENCHANTED GEAR RENTALS — TERMS OF SERVICE\n\n1. BOOKING & PAYMENT\nAll rentals are confirmed only after payment is received. A 3-day minimum applies to all rentals.\n\n2. CANCELLATION POLICY\n• 10+ days before your rental start date: FULL refund\n• 4-9 days before your rental start date: 50% refund\n• 3 days or fewer before start date: NO refund\nWeather or illness exceptions considered case-by-case — reach out and we'll work with you.\n\n3. DELIVERY\nWe deliver free to every Walt Disney World resort, Bonnet Creek, Universal, Disney Springs Area hotels, and vacation rentals in our service area. Same-day deliveries require a 2-hour lead time.\n\n4. EQUIPMENT CARE\nCustomers are responsible for equipment while in their possession. Excessive damage or loss may incur repair/replacement fees. Normal wear and tear is expected and covered.\n\n5. SAFETY\nAll equipment is inspected and sanitized between every rental. Customers agree to use equipment as intended and per manufacturer guidelines.\n\n6. CONTACT & COMMUNICATION\nBy booking, you consent to receive booking-related emails. SMS text updates are opt-in on your booking form; automated order updates are sent from 833-805-GEAR (4327). For customer service, call or text 689-345-RENT (7368). Reply STOP to unsubscribe from SMS at any time.\n\n7. CHANGES TO THIS POLICY\nWe may update these terms occasionally; the version in effect at the time of your booking applies to your rental.\n\nEnchanted Gear Rentals is family-owned and independent. Not affiliated with The Walt Disney Company." },
    ],
  },
];

export const SiteContent = () => {
  const [remote, setRemote] = useState({});
  const [drafts, setDrafts] = useState({});
  const [savingKey, setSavingKey] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/site-content`);
      setRemote(data.content || {});
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't load current content");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const currentValue = (item) => {
    if (drafts[item.key] !== undefined) return drafts[item.key];
    if (remote[item.key] !== undefined && remote[item.key] !== "") return remote[item.key];
    return item.fallback;
  };
  const isDirty = (item) => drafts[item.key] !== undefined && drafts[item.key] !== (remote[item.key] ?? item.fallback);
  const isOverridden = (item) => remote[item.key] !== undefined && remote[item.key] !== "";

  const save = async (item) => {
    setSavingKey(item.key);
    try {
      await axios.patch(`${API}/admin/site-content/${item.key}`, { value: drafts[item.key] });
      toast.success("Saved");
      setDrafts(({ [item.key]: _, ...rest }) => rest);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSavingKey(null); }
  };

  const reset = async (item) => {
    if (!window.confirm(`Reset "${item.label}" to the built-in default? Your override will be deleted.`)) return;
    try {
      await axios.delete(`${API}/admin/site-content/${item.key}`);
      toast.success("Reset to default");
      setDrafts(({ [item.key]: _, ...rest }) => rest);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Reset failed"); }
  };

  return (
    <div className="max-w-5xl mx-auto px-5 py-8 space-y-6" data-testid="admin-content-page">
      <div className="flex items-center gap-3">
        <FileText className="w-6 h-6 text-[#FFD166]" />
        <h1 className="font-serif text-3xl">Homepage content</h1>
      </div>
      <p className="text-sm text-[#B9C8D5] max-w-2xl">
        Edit any headline, subtitle, paragraph, or button label shown on the public homepage —
        Hero, Why Us, Stats, Trip Setup CTA, Catalog tiles, Booking widget, Delivery, Testimonials, Blog, FAQ, Contact, Footer, and Legal Terms.
        Changes go live immediately after saving. Leave a field on its default (or hit <b className="text-[#FFD166]">Reset</b>) to fall back to the built-in copy.
      </p>

      {loading && <p className="text-[#B9C8D5]">Loading…</p>}

      {!loading && CONTENT_KEYS.map((sec) => (
        <Card key={sec.section}>
          <div className="p-5">
            <h2 className="font-serif text-xl mb-4 text-[#FFD166]">{sec.section}</h2>
            <div className="space-y-4">
              {sec.keys.map((item) => (
                <div key={item.key} className="border-b border-[#FFD166]/10 pb-4 last:border-b-0 last:pb-0" data-testid={`content-row-${item.key}`}>
                  <div className="flex flex-wrap items-baseline justify-between gap-2 mb-1">
                    <label className="text-sm font-medium text-[#F6F1E4]">{item.label}</label>
                    <code className="text-[10px] text-[#B9C8D5]/60">{item.key}</code>
                  </div>
                  {item.multi ? (
                    <textarea
                      value={currentValue(item)}
                      onChange={(e) => setDrafts({ ...drafts, [item.key]: e.target.value })}
                      className="input min-h-[96px] leading-relaxed"
                      data-testid={`content-input-${item.key}`}
                    />
                  ) : (
                    <input
                      value={currentValue(item)}
                      onChange={(e) => setDrafts({ ...drafts, [item.key]: e.target.value })}
                      className="input"
                      data-testid={`content-input-${item.key}`}
                    />
                  )}
                  <div className="mt-2 flex items-center gap-2">
                    <Btn disabled={!isDirty(item) || savingKey === item.key} onClick={() => save(item)} data-testid={`content-save-${item.key}`}>
                      <Save className="w-4 h-4" /> {savingKey === item.key ? "Saving…" : "Save"}
                    </Btn>
                    {isOverridden(item) && (
                      <Btn variant="ghost" onClick={() => reset(item)} data-testid={`content-reset-${item.key}`}>
                        <RefreshCw className="w-4 h-4" /> Reset to default
                      </Btn>
                    )}
                    {isDirty(item) && <span className="text-[11px] text-[#FFD166]">Unsaved changes</span>}
                    {!isDirty(item) && isOverridden(item) && <span className="text-[11px] text-[#7EE787]">✓ Overridden</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
};
